import React, { useState } from 'react';
import { Shield, Copy, Check, X as XIcon, KeyRound, Smartphone, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Guided TOTP 2FA setup for public users.
 * Step 1: Show QR code + secret to add to authenticator app.
 * Step 2: User enters 6-digit code to verify + activate.
 */
const TwoFactorSetupModal = ({ open, onClose, onEnabled }) => {
  const [step, setStep] = useState('load'); // 'load' | 'scan' | 'verify' | 'done'
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const start = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('user_token');
      const r = await axios.post(
        `${BACKEND_URL}/api/user-2fa/enable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQr(r.data.qr_code);
      setSecret(r.data.secret);
      setStep('scan');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('user_token');
      await axios.post(
        `${BACKEND_URL}/api/user-2fa/verify-setup`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStep('done');
      toast.success('Two-factor authentication enabled');
      onEnabled?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  // Kick off on first open
  React.useEffect(() => {
    if (open && step === 'load') start();
    if (!open) {
      // Reset local state when closed
      setStep('load'); setQr(''); setSecret(''); setCode(''); setError(''); setCopied(false);
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0A0A14] border border-white/[0.08] text-white max-w-md rounded-3xl p-0 overflow-hidden" data-testid="user-2fa-setup-modal">
        <DialogHeader className="px-7 pt-7 pb-4 border-b border-white/[0.06]">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <DialogTitle className="text-white text-base font-medium">Enable two-factor authentication</DialogTitle>
              <p className="text-white/50 text-xs mt-1">Scan the QR code with Google Authenticator, Authy, or 1Password.</p>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div className="mx-7 mt-4 flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/25" data-testid="user-2fa-setup-error">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-red-300 text-xs">{error}</span>
          </div>
        )}

        {step === 'load' && (
          <div className="p-8 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-white/20 border-t-[#00E5FF] rounded-full animate-spin" />
          </div>
        )}

        {step === 'scan' && (
          <div className="px-7 py-6 space-y-5">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
              <Smartphone className="w-4 h-4 text-[#00E5FF] flex-shrink-0" />
              <p className="text-white/60 text-xs">Step 1 — Open your authenticator app and add a new account.</p>
            </div>
            <div className="flex justify-center">
              <div className="p-3 bg-white rounded-2xl">
                {qr && <img src={qr} alt="2FA QR code" className="w-48 h-48" data-testid="user-2fa-qr-image" />}
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-white/30 mb-2 font-medium">Or enter this secret manually</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/80 text-xs font-mono tracking-wider break-all" data-testid="user-2fa-secret">
                  {secret}
                </code>
                <button
                  type="button"
                  onClick={copySecret}
                  data-testid="user-2fa-copy-secret-btn"
                  className="px-3 py-2.5 bg-white/[0.05] border border-white/[0.08] rounded-xl hover:bg-white/[0.08] transition-all"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4 text-white/60" />}
                </button>
              </div>
            </div>
            <Button
              onClick={() => setStep('verify')}
              data-testid="user-2fa-scanned-btn"
              className="w-full bg-white text-black hover:bg-white/90 font-medium py-3 rounded-xl text-sm"
            >
              I've scanned it, continue
            </Button>
          </div>
        )}

        {step === 'verify' && (
          <form onSubmit={verify} className="px-7 py-6 space-y-5">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
              <KeyRound className="w-4 h-4 text-[#00E5FF] flex-shrink-0" />
              <p className="text-white/60 text-xs">Step 2 — Enter the 6-digit code from your app to confirm.</p>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-white/30 mb-2 font-medium">Verification code</label>
              <input
                type="text"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="user-2fa-verify-input"
                className="w-full text-center text-2xl tracking-[0.6em] font-mono px-4 py-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-emerald-400/50 focus:border-emerald-400/40 transition-all"
                placeholder="000000"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStep('scan')}
                className="px-5 py-2.5 rounded-xl text-white/50 hover:text-white/80 text-sm transition-colors"
              >
                Back
              </button>
              <Button
                type="submit"
                disabled={code.length !== 6 || loading}
                data-testid="user-2fa-verify-btn"
                className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-medium py-2.5 rounded-xl text-sm disabled:opacity-30"
              >
                {loading ? 'Verifying…' : 'Verify & enable'}
              </Button>
            </div>
          </form>
        )}

        {step === 'done' && (
          <div className="px-7 py-8 text-center space-y-4">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Check className="w-7 h-7 text-emerald-300" />
            </div>
            <div>
              <p className="text-white text-base font-medium">You're protected</p>
              <p className="text-white/50 text-xs mt-1 max-w-xs mx-auto">
                Next time you sign in, you'll be asked for a 6-digit code from your authenticator app.
              </p>
            </div>
            <Button
              onClick={onClose}
              data-testid="user-2fa-done-btn"
              className="w-full bg-white text-black hover:bg-white/90 font-medium py-3 rounded-xl text-sm"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorSetupModal;
